import { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import { venueApi } from '../api';
import { VENUE_STATUSES, VENUE_STATUS_COLORS } from '../constants';

interface TimeSlot {
  timeSlotIdx: number;
  time: string;
  label: string;
}

interface Venue {
  venueIdx: number;
  venueName: string;
  floor: string;
  capacityMin: number;
  capacityMax: number;
  status: string;
  memo: string;
  timeSlots?: TimeSlot[];
}

export default function WeddingHallVenues() {
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(false);

  // 모달
  const [showModal, setShowModal] = useState(false);
  const [editingVenue, setEditingVenue] = useState<Venue | null>(null);
  const [form, setForm] = useState({
    venueName: '',
    floor: '',
    capacityMin: '',
    capacityMax: '',
    status: 'ACTIVE',
    memo: '',
  });

  // 타임슬롯 추가
  const [addingSlotVenueIdx, setAddingSlotVenueIdx] = useState<number | null>(null);
  const [newSlotTime, setNewSlotTime] = useState('');
  const [newSlotLabel, setNewSlotLabel] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await venueApi.list();
      const venueList: Venue[] = result.items || result || [];

      // 각 홀의 타임슬롯 로드
      const withSlots = await Promise.all(
        venueList.map(async (v: Venue) => {
          try {
            const slots = await venueApi.timeSlotList(v.venueIdx);
            return { ...v, timeSlots: slots.items || slots || [] };
          } catch {
            return { ...v, timeSlots: [] };
          }
        })
      );
      setVenues(withSlots);
    } catch (err) {
      console.error('홀 목록 조회 실패:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // 모달 열기/닫기
  const openAddModal = () => {
    setEditingVenue(null);
    setForm({ venueName: '', floor: '', capacityMin: '', capacityMax: '', status: 'ACTIVE', memo: '' });
    setShowModal(true);
  };

  const openEditModal = (venue: Venue) => {
    setEditingVenue(venue);
    setForm({
      venueName: venue.venueName || '',
      floor: venue.floor || '',
      capacityMin: String(venue.capacityMin ?? ''),
      capacityMax: String(venue.capacityMax ?? ''),
      status: venue.status || 'ACTIVE',
      memo: venue.memo || '',
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingVenue(null);
  };

  // 저장
  const handleSave = async () => {
    if (!form.venueName.trim()) { alert('홀 이름을 입력해주세요.'); return; }
    try {
      const payload = {
        venueName: form.venueName,
        floor: form.floor,
        capacityMin: form.capacityMin ? Number(form.capacityMin) : undefined,
        capacityMax: form.capacityMax ? Number(form.capacityMax) : undefined,
        status: form.status,
        memo: form.memo,
      };
      if (editingVenue) {
        await venueApi.update(editingVenue.venueIdx, payload);
      } else {
        await venueApi.create(payload);
      }
      closeModal();
      fetchData();
    } catch (err: any) {
      alert(`저장 실패: ${err.message}`);
    }
  };

  // 삭제
  const handleDelete = async (venue: Venue) => {
    if (!confirm(`"${venue.venueName}" 홀을 삭제하시겠습니까?`)) return;
    try {
      await venueApi.delete(venue.venueIdx);
      fetchData();
    } catch (err: any) {
      alert(`삭제 실패: ${err.message}`);
    }
  };

  // 타임슬롯 추가
  const handleAddTimeSlot = async (venueIdx: number) => {
    if (!newSlotTime) { alert('시간을 입력해주세요.'); return; }
    if (!newSlotLabel.trim()) { alert('부 이름을 입력해주세요.'); return; }
    try {
      await venueApi.timeSlotCreate(venueIdx, { time: newSlotTime, label: newSlotLabel });
      setAddingSlotVenueIdx(null);
      setNewSlotTime('');
      setNewSlotLabel('');
      fetchData();
    } catch (err: any) {
      alert(`타임슬롯 추가 실패: ${err.message}`);
    }
  };

  // 타임슬롯 삭제
  const handleDeleteTimeSlot = async (slotIdx: number) => {
    if (!confirm('이 타임슬롯을 삭제하시겠습니까?')) return;
    try {
      await venueApi.timeSlotDelete(slotIdx);
      fetchData();
    } catch (err: any) {
      alert(`타임슬롯 삭제 실패: ${err.message}`);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusObj = VENUE_STATUSES.find(s => s.value === status);
    const colorClass = VENUE_STATUS_COLORS[status] || 'bg-gray-100 text-gray-700';
    return (
      <span className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full ${colorClass}`}>
        {statusObj?.label || status}
      </span>
    );
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 p-8">

        {/* 헤더 */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">홀 관리</h1>
            <p className="text-sm text-gray-600 mt-1">예식홀 및 타임슬롯을 관리합니다</p>
          </div>
          <button onClick={openAddModal}
            className="px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors cursor-pointer flex items-center gap-2">
            <i className="ri-add-line"></i>홀 추가
          </button>
        </div>

        {/* 홀 카드 그리드 */}
        {loading ? (
          <div className="flex justify-center p-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>
        ) : venues.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-16 text-center text-gray-400">
            <i className="ri-building-2-line text-5xl block mb-3"></i>등록된 홀이 없습니다.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {venues.map((venue) => (
              <div key={venue.venueIdx} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                {/* 카드 헤더 */}
                <div className="bg-blue-600 px-5 py-4 flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-white">{venue.venueName}</h3>
                    {venue.floor && <p className="text-blue-200 text-sm mt-0.5">{venue.floor}</p>}
                  </div>
                  {getStatusBadge(venue.status)}
                </div>

                {/* 카드 바디 */}
                <div className="p-5 space-y-4">
                  {/* 수용인원 */}
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <i className="ri-group-line text-blue-500"></i>
                    <span>수용인원</span>
                    <span className="font-semibold text-gray-800">
                      {venue.capacityMin ?? '-'}~{venue.capacityMax ?? '-'}명
                    </span>
                  </div>

                  {/* 메모 */}
                  {venue.memo && (
                    <div className="text-xs text-gray-500 bg-gray-50 rounded-lg p-3">
                      <i className="ri-sticky-note-line mr-1"></i>{venue.memo}
                    </div>
                  )}

                  {/* 타임슬롯 */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">타임슬롯</span>
                      <button onClick={() => {
                        setAddingSlotVenueIdx(addingSlotVenueIdx === venue.venueIdx ? null : venue.venueIdx);
                        setNewSlotTime('');
                        setNewSlotLabel('');
                      }}
                        className="text-xs text-blue-600 hover:text-blue-800 cursor-pointer flex items-center gap-0.5">
                        <i className="ri-add-circle-line"></i>추가
                      </button>
                    </div>

                    {venue.timeSlots && venue.timeSlots.length > 0 ? (
                      <div className="space-y-1.5">
                        {venue.timeSlots.map((slot) => (
                          <div key={slot.timeSlotIdx}
                            className="flex items-center justify-between bg-blue-50 rounded-lg px-3 py-2">
                            <div className="flex items-center gap-2 text-sm">
                              <i className="ri-time-line text-blue-500"></i>
                              <span className="font-medium text-gray-800">{slot.time?.substring(0, 5)}</span>
                              <span className="text-gray-500">{slot.label}</span>
                            </div>
                            <button onClick={() => handleDeleteTimeSlot(slot.timeSlotIdx)}
                              className="text-gray-400 hover:text-red-500 cursor-pointer">
                              <i className="ri-close-line text-sm"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400">등록된 타임슬롯이 없습니다</p>
                    )}

                    {/* 타임슬롯 추가 인라인 폼 */}
                    {addingSlotVenueIdx === venue.venueIdx && (
                      <div className="mt-2 flex items-center gap-2">
                        <input type="time" value={newSlotTime} onChange={(e) => setNewSlotTime(e.target.value)}
                          className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-[110px]" />
                        <input type="text" placeholder="예: 1부" value={newSlotLabel} onChange={(e) => setNewSlotLabel(e.target.value)}
                          className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-[80px]" />
                        <button onClick={() => handleAddTimeSlot(venue.venueIdx)}
                          className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 cursor-pointer">
                          확인
                        </button>
                        <button onClick={() => setAddingSlotVenueIdx(null)}
                          className="px-3 py-1.5 bg-gray-200 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-300 cursor-pointer">
                          취소
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* 카드 푸터 */}
                <div className="border-t border-gray-100 px-5 py-3 flex items-center justify-end gap-2">
                  <button onClick={() => openEditModal(venue)}
                    className="px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer flex items-center gap-1">
                    <i className="ri-edit-line"></i>수정
                  </button>
                  <button onClick={() => handleDelete(venue)}
                    className="px-3 py-1.5 text-sm text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer flex items-center gap-1">
                    <i className="ri-delete-bin-line"></i>삭제
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* 추가/수정 모달 */}
        {showModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={closeModal}>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
              <div className="bg-blue-600 px-6 py-4 rounded-t-xl">
                <h2 className="text-lg font-bold text-white">
                  {editingVenue ? '홀 수정' : '홀 추가'}
                </h2>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">홀 이름 <span className="text-red-500">*</span></label>
                  <input type="text" value={form.venueName} onChange={(e) => setForm({ ...form, venueName: e.target.value })}
                    placeholder="예: 그랜드볼룸"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">층</label>
                  <input type="text" value={form.floor} onChange={(e) => setForm({ ...form, floor: e.target.value })}
                    placeholder="예: 2층"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">최소 인원</label>
                    <input type="number" value={form.capacityMin} onChange={(e) => setForm({ ...form, capacityMin: e.target.value })}
                      placeholder="100"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">최대 인원</label>
                    <input type="number" value={form.capacityMax} onChange={(e) => setForm({ ...form, capacityMax: e.target.value })}
                      placeholder="300"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">상태</label>
                  <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    {VENUE_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">메모</label>
                  <textarea value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })}
                    rows={3} placeholder="메모를 입력해주세요"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
                <button onClick={closeModal}
                  className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer">
                  취소
                </button>
                <button onClick={handleSave}
                  className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors cursor-pointer">
                  {editingVenue ? '수정' : '추가'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
